import { Row } from '@tanstack/react-table'
import { commonActionColumn } from '@utils/assessmentReportUtils'
import { Progress } from 'antd'

import { Database_Procs_TeamBonusGames_Item } from '@suntex.fim/api-client'
import { ColumnType, Tooltip } from '@suntex.fim/ui'

import {
  generateScrollableAssessmentCommonColumns,
  renderWrappedAssessmentHeader
} from './CommonColumns'

/**
 * handles hidable columns list
 */
export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  pk_three_at_a_aime: 'Pre-K Three at a Time',
  pk_how_many: 'Pre-K How Many',
  pk_before_and_after: 'Pre-K Before & After',
  pk_mol: 'Pre-K More or Less',
  pk_missing_icons: 'Pre-K Missing Icons',
  link_n_lock_mosaic: 'Link N Lock Mosaic',
  pk_memory_tiles: 'Memory Tiles',
  k2_triplets: 'Triplets',
  see_3: 'See 3',
  k2_rps: 'K-2 RPS Chess',
  k2_misfits: 'K-2 Misfits',
  k2_pundis_puzzle: `K-2 Pundi's Puzzle`,
  k2_jennies_jewels: `Jennie's Jewels`,
  link_n_lock_jewels: 'Link N Lock Jewels',
  gems_to_zero: 'Gems to Zero',
  jeweled_tiles: 'Jeweled Tiles',
  jtf_k2_add: 'K-2 JTF Add',
  jtf_k2_sub: 'K-2 JTF Sub',
  k2_skip_game: 'K-2 Skip Game',
  k2_mol: 'K-2 More or Less',
  _3_to_9_add: '3 to 9 Add',
  _3_to_9_sub: '3 to 9 Sub',
  k2_how_many: 'How Many?',
  k2_misfits_num: 'K-2 Misfits Num',
  k2_four_at_a_time: 'K-2 Four at a Time',
  k2_five_at_a_time: 'K-2 Five at a Time',
  k2_undo: 'K-2 Undo',
  k2_before_and_after: 'K-2 Before and After',
  k2_pundis_puzzle_ct: `K-2 Pundi's CT`,
  k2_rps_ct: 'K-2 RPS Chess CT',
  k2_monus_path_ct: `K-2 Monu's Path Coding`,
  k2_misfits_num_ct: 'K-2 Number Misfits Coding',
  k2_missing_icons_ct: 'K-2 Coding Missing Icons',
  k2_what_belongs_ct: 'K-2 What Belongs',
  abc: 'ABC',
  letter_memory: 'Letter Memory',
  matching_letters: 'Matching Letters',
  alphabet: 'Alphabet',
  sound_of_letters: 'Sound of Letters',
  phonemes_alpha: 'Phonemes Alpha',
  phonemes_pi: 'Phonemes Pi',
  cb_sounds: 'CB Sounds',
  consonant_blends: 'Consonant Blends',
  high_fq_words: 'High FQ Words',
  rhyme_time: 'Rhyme Time',
  change_one_letter: 'Change a Letter',
  misfits_spelling: 'Misfits Spelling',
  missing_letters: 'Missing Letters',
  verb_tenses: 'Verb Tenses',
  building_sentences: 'Building Sentences',
  word_wheels: 'Word Wheels',
  finding_words_4x4: 'Finding Words 4x4',
  write_a_sentence: 'Write a Sentence'
}

export const getEarlyColumnKeys = (level: number) => {
  // Initialize the columnKeys
  let columnKeys = [
    'pk_three_at_a_aime',
    'pk_how_many',
    'pk_before_and_after',
    'pk_mol',
    'pk_missing_icons',
    'link_n_lock_mosaic',
    'pk_memory_tiles'
  ]
  // As per the level modify the columnKeys
  switch (level) {
    case 1:
      columnKeys = [
        'k2_triplets',
        'see_3',
        'k2_rps',
        'k2_misfits',
        'k2_pundis_puzzle',
        'k2_jennies_jewels',
        'link_n_lock_jewels',
        'gems_to_zero',
        'jeweled_tiles'
      ]
      break
    case 2:
      columnKeys = [
        'jtf_k2_add',
        'jtf_k2_sub',
        'k2_skip_game',
        'k2_mol',
        '_3_to_9_add',
        '_3_to_9_sub',
        'k2_how_many',
        'k2_misfits_num',
        'k2_four_at_a_time',
        'k2_five_at_a_time',
        'k2_undo',
        'k2_before_and_after'
      ]
      break
    case 3:
      columnKeys = [
        'k2_pundis_puzzle_ct',
        'k2_rps_ct',
        'k2_monus_path_ct',
        'k2_misfits_num_ct',
        'k2_missing_icons_ct',
        'k2_what_belongs_ct'
      ]
      break
    case 4:
      columnKeys = [
        'abc',
        'letter_memory',
        'matching_letters',
        'alphabet',
        'sound_of_letters',
        'phonemes_alpha',
        'phonemes_pi',
        'cb_sounds',
        'consonant_blends'
      ]
      break
    case 5:
      columnKeys = [
        'high_fq_words',
        'rhyme_time',
        'change_one_letter',
        'misfits_spelling',
        'missing_letters',
        'word_wheels',
        'finding_words_4x4'
      ]
      break
    case 6:
      columnKeys = ['verb_tenses', 'building_sentences', 'write_a_sentence']
      break
    default:
      columnKeys = [
        'pk_three_at_a_aime',
        'pk_how_many',
        'pk_before_and_after',
        'pk_mol',
        'pk_missing_icons',
        'link_n_lock_mosaic',
        'pk_memory_tiles'
      ]
      break
  }
  return columnKeys
}
/**
 * returns JSX for early report table
 */
function columns({
  level,
  graphColor,
  sendMessage
}: {
  level: number
  graphColor: string
  sendMessage: (t: Row<Database_Procs_TeamBonusGames_Item>[]) => void
}) {
  const columnKeys = getEarlyColumnKeys(level)

  /**
   * renders progress bar in table cell
   */
  const levelColumns: ColumnType<Database_Procs_TeamBonusGames_Item>[] =
    columnKeys.map((key) => ({
      header: () => renderWrappedAssessmentHeader(`${hidableSelectLabel[key]}`),
      accessorKey: key,
      id: key,
      size: 110,
      cell: (info) => (
        <Tooltip
          title={`${Number(info.getValue()) || 0}% complete`}
          classNames={{ root: 'progress-tooltip' }}
          arrow={false}
          placement="right"
        >
          <Progress
            strokeColor={graphColor}
            percent={Number(info.getValue()) || 0}
            size={['100%', 32]}
            showInfo={false}
          />
        </Tooltip>
      )
    }))
  const actioncolumn = commonActionColumn<Database_Procs_TeamBonusGames_Item>(
    hidableSelectLabel,
    sendMessage
  )
  /**
   * handles table column mapping
   */
  const finalColumns: ColumnType<Database_Procs_TeamBonusGames_Item>[] = [
    actioncolumn,
    ...generateScrollableAssessmentCommonColumns<Database_Procs_TeamBonusGames_Item>(),
    ...levelColumns
  ]

  return finalColumns
}

export default columns
